import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment-timezone';

import Paginator from '../../components/Paginator';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import ConsumptionTableHeader from './ConsumptionTableHeader';

const consumptionTypesMsg = defineMessages({
  assessment: { id: 'consumption.consumption_type.assessment', defaultMessage: 'Assessment' },
  multiAssessment: {
    id: 'consumption.consumption_type.multi-assessment',
    defaultMessage: 'Assessments',
  },
  survey: { id: 'consumption.consumption_type.survey', defaultMessage: 'Survey' },
  multiSurvey: {
    id: 'consumption.consumption_type.multi-survey',
    defaultMessage: 'Surveys',
  },
});

const messages = defineMessages({
  type: { id: 'consumption.account_consumption.data_table.headers.type', defaultMessage: 'Type' },
  user: { id: 'consumption.account_consumption.data_table.headers.user', defaultMessage: 'User' },
  respondent: { id: 'consumption.account_consumption.data_table.headers.respondent', defaultMessage: 'Name' },
  template: { id: 'consumption.account_consumption.data_table.headers.template', defaultMessage: 'Template' },
  amount: { id: 'consumption.account_consumption.data_table.headers.amount', defaultMessage: 'Amount' },
  consumedOn: { id: 'consumption.account_consumption.data_table.headers.consumedOn', defaultMessage: 'Consumed on' },
  details: { id: 'consumption.account_consumption.data_table.body.details', defaultMessage: 'Details' },
  dataNotAvailable: {
    id: 'consumption.account_consumption.data_table.body.dataNotAvailable',
    defaultMessage: 'Data not available',
  },
});

const ConsumptionList = ({ connection, currentAccount, loading, locale }) => {
  const history = useHistory();
  const { location } = history;
  const { formatMessage, formatDate } = useIntl();

  const { edges, totalCount, indexFrom, indexTo, pageInfo } = connection;

  const rows = edges.map((edge) => {
    const { node } = edge;
    const { consumedBy } = node;
    const fullName = consumedBy ? `${consumedBy.firstName} - ${consumedBy.lastName}` : '';
    return {
      ...node,
      fullName,
    };
  });

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const typeHeader = formatMessage(messages.type);

  const userHeader = formatMessage(messages.user);
  const respondentHeader = formatMessage(messages.respondent);
  const templateHeader = formatMessage(messages.template);
  const consumedCreditsHeader = formatMessage(messages.amount);
  const consumedOnHeader = formatMessage(messages.consumedOn);

  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  const detailsTemplate = (rowData) => {
    const buttonLabel = formatMessage(messages.details);
    const url = `/accounts/${currentAccount}/consumption/${rowData.id}`;

    return <Button label={buttonLabel} className="p-button-secondary" onClick={() => history.push(url)} />;
  };

  const respondentTemplate = (rowData) => {
    if (rowData.respondent === null) {
      return formatMessage(messages.dataNotAvailable);
    }

    return `${rowData.respondent.firstName} ${rowData.respondent.lastName}`;
  };

  const template = (rowData, column) => {
    const dataNotAvailable = formatMessage(messages.dataNotAvailable);

    if (rowData.template === null) {
      return dataNotAvailable;
    }

    switch (rowData.type) {
      case 'ASSESSMENT':
      case 'MULTI_ASSESSMENT':
      case 'SURVEY':
      case 'MULTI_SURVEY':
        return rowData.template.title;
      default:
        return dataNotAvailable;
    }
  };

  const consumedTemplate = (rowData) => {
    const consumedOn = formatDate(moment(rowData.consumedOn), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
    return consumedOn;
  };

  const typeTemplate = (rowData) => {
    switch (rowData.type) {
      case 'ASSESSMENT':
        return formatMessage(consumptionTypesMsg.assessment);
      case 'MULTI_ASSESSMENT':
        return formatMessage(consumptionTypesMsg.multiAssessment);
      case 'SURVEY':
        return formatMessage(consumptionTypesMsg.survey);
      case 'MULTI_SURVEY':
        return formatMessage(consumptionTypesMsg.multiSurvey);
      default:
        return '??';
    }
  };

  const userTemplate = (rowData) => {
    const { consumedBy } = rowData;

    if (consumedBy) {
      return <Link to={`/accounts/${currentAccount}/users/${consumedBy.id}`}>{rowData.fullName}</Link>;
    }
    return <p>{formatMessage(messages.dataNotAvailable)}</p>;
  };

  const amountTemplate = (rowData) => {
    const { consumedCredits } = rowData;

    const formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 });

    return formatter.format(consumedCredits);
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <ConsumptionTableHeader />
              </div>
              <div className="p-col-12">
                <DataTable
                  value={rows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={(event) => sortUrl(event, location, history)}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column field="type" header={typeHeader} body={typeTemplate} style={{ width: '100px' }} sortable />
                  <Column field="user" header={userHeader} body={userTemplate} />
                  <Column field="respondent" header={respondentHeader} body={respondentTemplate} />
                  <Column field="template" header={templateHeader} body={template} />
                  <Column
                    field="consumedCredits"
                    header={consumedCreditsHeader}
                    body={amountTemplate}
                    style={{ width: '80px' }}
                  />
                  <Column
                    field="consumedOn"
                    header={consumedOnHeader}
                    sortable
                    body={consumedTemplate}
                    style={{ width: '110px' }}
                  />
                  <Column field="details" body={detailsTemplate} />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

ConsumptionList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

export default ConsumptionList;
